// @flow
//
// Internal to `@uniflowed/router`: the React-free route table primitives.
//
// Server Components need to be able to name routes, throw router control
// errors, and match generated tables without importing the client router,
// React components, or hooks. Keep this file to data, errors, and pure
// functions; rendering belongs in `runtime.js`.

/** One parameter a route path captures. */
export type RouteParamSpec = {| readonly name: string, readonly catchAll: boolean |};

/**
 * The parameters captured from a URL. A catch-all captures the rest as a list:
 * `[...slug]` at least one segment, `[[...slug]]` any number, none included.
 */
export type RouteParams = { readonly [string]: string | $ReadOnlyArray<string> };

/** The query string, as a read-only map. A repeated key keeps its last value. */
export type SearchParams = { readonly [string]: string };

/**
 * The query string with every value of a repeated key kept, in order:
 * `?tag=a&tag=b` is `{ tag: ["a", "b"] }`. See [`parseSearchAll`].
 */
export type SearchParamsAll = { readonly [string]: $ReadOnlyArray<string> };

/**
 * One reason a query did not fit the schema a page declared for it.
 *
 * The shape of `@uniflowed/validator`'s `Issue`, written out here so that this
 * file stays free of imports: `path` is where in the query the problem is —
 * `["tags", "1"]` for the second `tag`.
 */
export type SearchParamsIssue = {|
  readonly code   : string,
  readonly message: string,
  readonly path?  : $ReadOnlyArray<string>,
|};

/** A lazy route module entry from the generated route table. */
export type RouteModule<TModule = mixed> = () => Promise<TModule>;

/** One `$template.js`, as the route table carries it. */
export type TemplateRecord<TTemplate = mixed> = {|
  readonly above : number,
  readonly module: RouteModule<TTemplate>,
|};

/** One `$loading.js`, as the route table carries it. */
export type LoadingRecord<TLoading = mixed> = {|
  readonly above : number,
  readonly module: RouteModule<TLoading>,
|};

/** One `$error.js` inside a slot, as the route table carries it. */
export type SlotErrorBoundaryRecord<TError = mixed> = {|
  readonly above : number,
  readonly module: RouteModule<TError>,
|};

/** One parallel-route slot, as the route table carries it. */
export type SlotRecord<
  TPage = mixed,
  TLayout = mixed,
  TTemplate = mixed,
  TLoading = mixed,
  TError = mixed,
> = {|
  readonly name: string,
  readonly above: number,
  readonly defaultPage: ?RouteModule<TPage>,
  readonly defaultFile?: string,
  readonly defaultMdx?: boolean,
  readonly defaultErrorBoundary?: ?SlotErrorBoundaryRecord<TError>,
  readonly routes: $ReadOnlyArray<SlotRouteRecord<TPage, TLayout, TTemplate, TLoading, TError>>,
  /**
   * The routes this slot renders when a client navigation *intercepts* the URL
   * each one names — `app/feed/@modal/(.)photo/[id]/$page.js` is the one for
   * `/feed/photo/:id` — keyed by that URL.
   *
   * A second list rather than a flag on `routes`, because the two are read by
   * different callers at different times and neither may see the other's.
   * `routes` is matched against every URL the segment renders, by the server and
   * by the browser alike. These are matched only by a navigation that starts on
   * a page this slot is already rendered on, and nothing on a server reads them
   * — which is the whole of why a document request for an intercepted URL
   * renders the ordinary page. `resolveInterception`, beside the rest of the
   * rendering, is the one reader.
   *
   * Absent on a slot that intercepts nothing, which is every slot a table
   * written before interception existed.
   */
  readonly intercepts?: $ReadOnlyArray<
    SlotRouteRecord<TPage, TLayout, TTemplate, TLoading, TError>,
  >,
|};

/** One page inside a slot. */
export type SlotRouteRecord<
  TPage = mixed,
  TLayout = mixed,
  TTemplate = mixed,
  TLoading = mixed,
  TError = mixed,
> = {|
  readonly path          : string,
  readonly params        : $ReadOnlyArray<RouteParamSpec>,
  readonly mdx           : boolean,
  readonly file          : string,
  readonly page          : RouteModule<TPage>,
  readonly layouts       : $ReadOnlyArray<RouteModule<TLayout>>,
  readonly loading?      : $ReadOnlyArray<LoadingRecord<TLoading>>,
  readonly templates?    : $ReadOnlyArray<TemplateRecord<TTemplate>>,
  readonly errorBoundary?: ?SlotErrorBoundaryRecord<TError>,
  readonly slots         : $ReadOnlyArray<SlotRecord<TPage, TLayout, TTemplate, TLoading, TError>>,
|};

/** One entry of the generated route table. */
export type RouteRecord<
  TPage = mixed,
  TLayout = mixed,
  TTemplate = mixed,
  TLoading = mixed,
  TError = mixed,
> = {|
  readonly path      : string,
  readonly params    : $ReadOnlyArray<RouteParamSpec>,
  readonly mdx       : boolean,
  readonly file      : string,
  readonly page?     : RouteModule<TPage>,
  readonly layouts   : $ReadOnlyArray<RouteModule<TLayout>>,
  readonly loading?  : $ReadOnlyArray<LoadingRecord<TLoading>>,
  readonly templates?: $ReadOnlyArray<TemplateRecord<TTemplate>>,
  readonly slots?    : $ReadOnlyArray<SlotRecord<TPage, TLayout, TTemplate, TLoading, TError>>,
|};

/** One not-found boundary: the page for a path under `path` that matched nothing. */
export type NotFoundBoundary<TPage = mixed, TLayout = mixed> = {|
  readonly path   : string,
  readonly mdx    : boolean,
  readonly file   : string,
  readonly page   : ?RouteModule<TPage>,
  readonly layouts: $ReadOnlyArray<RouteModule<TLayout>>,
|};

/** One error boundary: what renders in place of a subtree that threw. */
export type ErrorBoundary<TError = mixed, TLayout = mixed> = {|
  readonly path   : string,
  readonly file   : string,
  readonly module : ?RouteModule<TError>,
  readonly layouts: $ReadOnlyArray<RouteModule<TLayout>>,
|};

/** A route table plus the boundaries declared under it. */
export type RouteTable<
  TPage = mixed,
  TLayout = mixed,
  TTemplate = mixed,
  TLoading = mixed,
  TError = mixed,
> = {|
  readonly routes: $ReadOnlyArray<RouteRecord<TPage, TLayout, TTemplate, TLoading, TError>>,
  readonly nativeLinks?: {|
    readonly origins: $ReadOnlyArray<string>,
    readonly routes : $ReadOnlyArray<string>,
  |},
  readonly notFound: $ReadOnlyArray<NotFoundBoundary<TPage, TLayout>>,
  readonly errors  : $ReadOnlyArray<ErrorBoundary<TError, TLayout>>,
|};

type UnknownRouteRecord = RouteRecord<mixed, mixed, mixed, mixed, mixed>;

/** A URL matched against a table. */
export type RouteMatch<TRoute extends { readonly path: string, ... } = UnknownRouteRecord> = {|
  readonly route : TRoute,
  readonly params: RouteParams,
|};

/**
 * Why the router is rendering an error boundary instead of a page.
 *
 * One union rather than one file convention per status. `forbidden()` and
 * `unauthorized()` are not different *kinds* of file to write; they are
 * different sentences an error page says.
 */
export type RouteError =
  | {| readonly kind: "thrown", readonly error: mixed |}
  | {| readonly kind: "unauthorized" |}
  | {| readonly kind: "forbidden" |}
  /**
   * The query did not fit the schema the page exports as `searchParams`.
   * The request's fault rather than the page's, so a `400` — and the issues
   * say which parameter and why, so an `$error.js` can say so too.
   */
  | {| readonly kind: "badRequest", readonly issues: $ReadOnlyArray<SearchParamsIssue> |};

/** The status a `RouteError` answers with. */
export function routeErrorStatus(error: RouteError): 400 | 401 | 403 | 500 {
  return match (error) {
    {kind: "badRequest", ...} => 400,
    {kind: "unauthorized"}    => 401,
    {kind: "forbidden"}       => 403,
    {kind: "thrown", ...}     => 500,
  };
}

/** Thrown by `notFound()`; the renderer answers with the not-found page. */
export class NotFoundError extends Error {
  constructor() {
    super("not found");
    this.name = "NotFoundError";
  }
}

/** Thrown by `unauthorized()`; the renderer answers with the error boundary. */
export class UnauthorizedError extends Error {
  constructor() {
    super("unauthorized");
    this.name = "UnauthorizedError";
  }
}

/**
 * Thrown when a query does not fit the page's `searchParams` schema; the
 * renderer answers with the error boundary, as a `400`.
 */
export class SearchParamsError extends Error {
  issues: $ReadOnlyArray<SearchParamsIssue>;

  constructor(issues: $ReadOnlyArray<SearchParamsIssue>) {
    super(
      `the query string does not fit this page's searchParams schema: ${issues
        .map((issue) => `${(issue.path ?? []).join(".") || "(query)"}: ${issue.message}`)
        .join("; ")}`,
    );
    this.name = "SearchParamsError";
    this.issues = issues;
  }
}

/** Thrown by `forbidden()`; the renderer answers with the error boundary. */
export class ForbiddenError extends Error {
  constructor() {
    super("forbidden");
    this.name = "ForbiddenError";
  }
}

/**
 * The URL schemes a navigation never follows, because following one runs
 * script in the page rather than loading one.
 *
 * `javascript:` is the reason. `location.assign("javascript:…")` and
 * `location.replace("javascript:…")` execute it in the current document, with
 * the page's origin and its cookies, so a router that hands a caller's string
 * to either is a DOM XSS sink the moment that string comes from a query
 * parameter: `redirect(searchParams.get("next"))` is the sign-in flow in half
 * the applications ever written. React 19 refuses the same scheme in `href`
 * for the same reason, and `Link` renders through that; this is the half of
 * the router React never sees. `vbscript:` is the same thing in an older
 * browser, and `data:` a document of the sender's choosing that browsers now
 * refuse to navigate to at the top level anyway — refused here too, so the
 * answer does not depend on which browser was asked.
 */
const SCRIPT_SCHEMES: $ReadOnlyArray<string> = ["javascript:", "vbscript:", "data:"];

/**
 * The scheme `to` would run as script when navigated to, or `null` when it is
 * a URL a navigation may follow.
 *
 * Parsed with the WHATWG URL parser rather than compared as text, because the
 * browser is what decides and it forgives a great deal: leading spaces and
 * control characters are stripped, tabs and newlines anywhere are dropped, and
 * the scheme is case-insensitive — ` JaVa\tScRiPt:` is `javascript:`. The base
 * only resolves a relative path, which is never one of these.
 */
export function scriptSchemeOf(to: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(to, "http://uf.invalid/");
  } catch {
    return null;
  }
  return SCRIPT_SCHEMES.includes(parsed.protocol) ? parsed.protocol : null;
}

/**
 * Throw unless `to` is a URL a navigation may follow.
 *
 * Named after the call that asked (`redirect()`, `router.push()`), so the
 * stack a developer reads points at the line that built the URL. The URL
 * itself is not repeated: it is often a value a visitor chose, and an error
 * message is something applications log.
 */
export function refuseScriptUrl(to: string, caller: string): void {
  const scheme = scriptSchemeOf(to);
  if (scheme != null) {
    throw new Error(
      `@uniflowed/router: ${caller} refused a ${scheme} URL, which a browser would run as ` +
        "script in this page rather than load. If the URL came from a query parameter, allow " +
        "only paths on this site before navigating to it.",
    );
  }
}

/**
 * Thrown by `redirect()`; the renderer answers with a redirect.
 *
 * Refuses a script URL when it is built (see [`refuseScriptUrl`]), so every
 * place that follows one — the server's `Location`, the browser's
 * `location.replace` in a single-page application, a form's `303` — is
 * covered by one check rather than by one each.
 */
export class RedirectError extends Error {
  to: string;
  permanent: boolean;

  constructor(to: string, permanent: boolean) {
    refuseScriptUrl(to, permanent ? "permanentRedirect()" : "redirect()");
    super(`redirect to ${to}`);
    this.name = "RedirectError";
    this.to = to;
    this.permanent = permanent;
  }
}

type Segment =
  | {| readonly kind: "static", readonly value: string |}
  | {| readonly kind: "param", readonly name: string |}
  | {| readonly kind: "catchAll", readonly name: string |}
  | {| readonly kind: "optionalCatchAll", readonly name: string |};

/**
 * A route path, as segments: `posts` is static, `:slug` a parameter, `:slug*`
 * a catch-all (`[...slug]`) and `:slug*?` an optional one (`[[...slug]]`).
 */
function compile(routePath: string): $ReadOnlyArray<Segment> {
  return routePath
    .split("/")
    .filter((segment) => segment !== "")
    .map((segment): Segment => {
      if (segment.startsWith(":") && segment.endsWith("*?")) {
        return { kind: "optionalCatchAll", name: segment.slice(1, -2) };
      }
      if (segment.startsWith(":") && segment.endsWith("*")) {
        return { kind: "catchAll", name: segment.slice(1, -1) };
      }
      if (segment.startsWith(":")) {
        return { kind: "param", name: segment.slice(1) };
      }
      return { kind: "static", value: segment };
    });
}

/**
 * How specific a route is, for ranking: a static segment outranks a parameter,
 * which outranks a catch-all, which outranks an optional one, and a longer
 * path outranks a shorter one.
 */
function specificity(segments: $ReadOnlyArray<Segment>): number {
  let score = 0;
  for (const segment of segments) {
    score += match (segment) {
      {kind: "static", ...}           => 3,
      {kind: "param", ...}            => 2,
      {kind: "catchAll", ...}         => 1,
      {kind: "optionalCatchAll", ...} => 0,
    };
  }
  return score;
}

function matchSegments(
  segments: $ReadOnlyArray<Segment>,
  parts   : $ReadOnlyArray<string>,
): ?RouteParams {
  const params: { [string]: string | $ReadOnlyArray<string> } = {};
  let index = 0;
  for (const segment of segments) {
    match (segment) {
      {kind: "static", value: const value}         => {
        if (parts[index] !== value) {
          return null;
        }
        index += 1;
      }
      {kind: "param", name: const name}            => {
        if (index >= parts.length) {
          return null;
        }
        params[name] = decodeSegment(parts[index]);
        index += 1;
      }
      {kind: "catchAll", name: const name}         => {
        // `[...slug]` needs something to take: `/docs` is not
        // `/docs/[...slug]`, which is what `[[...slug]]` is for. Without
        // this a catch-all outranked the page at its parent path — one more
        // segment is one more point — and answered `/docs` in its place.
        if (index >= parts.length) {
          return null;
        }
        params[name] = parts.slice(index).map(decodeSegment);
        index = parts.length;
      }
      {kind: "optionalCatchAll", name: const name} => {
        params[name] = parts.slice(index).map(decodeSegment);
        index = parts.length;
      }
    }
  }
  return index === parts.length ? params : null;
}

/**
 * The URL for a route pattern and the parameters it takes.
 *
 * The inverse of [`matchSegments`], and deliberately built out of the same
 * [`compile`]: a builder that parsed patterns its own way would drift from the
 * matcher, and the drift would show up as a link that 404s rather than as a
 * failure anybody could see.
 */
export function buildRoute(routePath: string, params?: RouteParams): string {
  const values: RouteParams = params ?? {};
  const parts: Array<string> = [];
  for (const segment of compile(routePath)) {
    match (segment) {
      {kind: "static", value: const value}         => {
        parts.push(value);
      }
      {kind: "param", name: const name}            => {
        const value = values[name];
        if (typeof value !== "string") {
          throw new Error(
            `route ${routePath} takes a string for :${name}, and got ${describeParam(value)}`,
          );
        }
        parts.push(encodeURIComponent(value));
      }
      {kind: "catchAll", name: const name}         => {
        const value = values[name];
        if (value == null || typeof value === "string") {
          throw new Error(
            `route ${routePath} takes an array of segments for :${name}*, and got ` +
              describeParam(value),
          );
        }
        // The URL an empty list builds is the parent path, which this route
        // does not serve — a link that 404s. An optional catch-all does.
        if (value.length === 0) {
          throw new Error(
            `route ${routePath} takes at least one segment for :${name}*, and got an empty ` +
              `array; a route that also serves its parent path is [[...${name}]], :${name}*?`,
          );
        }
        for (const part of value) {
          parts.push(encodeURIComponent(part));
        }
      }
      {kind: "optionalCatchAll", name: const name} => {
        const value = values[name];
        if (value == null || typeof value === "string") {
          throw new Error(
            `route ${routePath} takes an array of segments for :${name}*?, and got ` +
              describeParam(value),
          );
        }
        for (const part of value) {
          parts.push(encodeURIComponent(part));
        }
      }
    }
  }
  return parts.length === 0 ? "/" : `/${parts.join("/")}`;
}

/** What a parameter was, for the message that says it was the wrong thing. */
function describeParam(value: string | $ReadOnlyArray<string> | void): string {
  if (value === undefined) {
    return "nothing";
  }
  return typeof value === "string" ? `the string ${JSON.stringify(value)}` : "an array";
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** Whether this table can render the route in the browser. */
export function hasClientPage(route: { readonly page?: mixed, ... }): boolean {
  return route.page != null;
}

/** Match a pathname against the table, preferring the most specific route. */
export function matchRoute<TRoute extends { readonly path: string, ... }>(
  routes  : $ReadOnlyArray<TRoute>,
  pathname: string,
): ?RouteMatch<TRoute> {
  return matchIn(routes, pathname);
}

/**
 * The same match, over anything that has a route path.
 *
 * A slot is a second table matched against the same URL, and it has to be
 * matched by this function rather than by one of its own.
 */
export function matchIn<TRoute extends { readonly path: string, ... }>(
  routes  : $ReadOnlyArray<TRoute>,
  pathname: string,
): ?RouteMatch<TRoute> {
  const parts = pathname.split("/").filter((part) => part !== "");
  let best: ?RouteMatch<TRoute> = null;
  let bestScore = -1;
  for (const route of routes) {
    const segments = compile(route.path);
    const params = matchSegments(segments, parts);
    if (params == null) {
      continue;
    }
    const score = specificity(segments);
    if (score > bestScore) {
      best = { route, params };
      bestScore = score;
    }
  }
  return best;
}

/**
 * Whether a boundary declared at `segments` is at or above `parts`.
 *
 * The same segment kinds as [`matchSegments`], stopping when the boundary's
 * own segments run out instead of requiring the path to.
 */
function covers(segments: $ReadOnlyArray<Segment>, parts: $ReadOnlyArray<string>): boolean {
  let index = 0;
  for (const segment of segments) {
    const next = match (segment) {
      {kind: "static", value: const value} => parts[index] === value ? index + 1 : -1,
      {kind: "param", ...}                 => index < parts.length ? index + 1 : -1,
      {kind: "catchAll", ...}              => index < parts.length ? parts.length : -1,
      {kind: "optionalCatchAll", ...}      => parts.length,
    };
    if (next === -1) {
      return false;
    }
    index = next;
  }
  return true;
}

/** The nearest boundary above `pathname`, or `null` when none covers it. */
export function nearestBoundary<TBoundary extends { readonly path: string, ... }>(
  boundaries: $ReadOnlyArray<TBoundary>,
  pathname  : string,
): ?TBoundary {
  const parts = pathname.split("/").filter((part) => part !== "");
  let best: ?TBoundary = null;
  let bestDepth = -1;
  for (const boundary of boundaries) {
    const segments = compile(boundary.path);
    if (!covers(segments, parts)) {
      continue;
    }
    if (segments.length > bestDepth) {
      best = boundary;
      bestDepth = segments.length;
    }
  }
  return best;
}

/** Split a URL into its pathname and search string. */
export function splitUrl(url: string): {| readonly pathname: string, readonly search: string |} {
  const hash = url.indexOf("#");
  const withoutHash = hash === -1 ? url : url.slice(0, hash);
  const question = withoutHash.indexOf("?");
  if (question === -1) {
    return { pathname: normalizePathname(withoutHash), search: "" };
  }
  return {
    pathname: normalizePathname(withoutHash.slice(0, question)),
    search  : withoutHash.slice(question),
  };
}

function normalizePathname(pathname: string): string {
  if (pathname === "" || pathname === "/") {
    return "/";
  }
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

/** Parse a search string into a flat map; a repeated key keeps its last value. */
export function parseSearch(search: string): SearchParams {
  const params: { [string]: string } = {};
  for (const [key, value] of new URLSearchParams(search)) {
    params[key] = value;
  }
  return params;
}

/**
 * Every value of every key in a query string, in order.
 *
 * [`parseSearch`] keeps the last value of a repeated key, which is what a page
 * that reads `searchParams.q` wants and loses `?tag=a&tag=b`'s first tag. This
 * is the same query with nothing dropped, for a page that declares no schema
 * and needs the repeats; a page that declares one gets arrays wherever the
 * schema says array.
 */
export function parseSearchAll(search: string): SearchParamsAll {
  // A `Map` and then `Object.fromEntries`, which defines own properties: a
  // query is written by whoever sent the request, and `?__proto__=x` must be a
  // key like any other rather than a lookup that finds `Object.prototype`.
  const params = new Map<string, Array<string>>();
  for (const [key, value] of new URLSearchParams(search)) {
    const values = params.get(key);
    if (values == null) {
      params.set(key, [value]);
    } else {
      values.push(value);
    }
  }
  return Object.fromEntries(params);
}

/** Stop rendering the current page and show the not-found page instead. */
export function notFound(): empty {
  throw new NotFoundError();
}

/** Stop rendering the current page and show the error boundary, as a 401. */
export function unauthorized(): empty {
  throw new UnauthorizedError();
}

/** Stop rendering the current page and show the error boundary, as a 403. */
export function forbidden(): empty {
  throw new ForbiddenError();
}

/** Stop rendering the current page and send the visitor elsewhere. */
export function redirect(to: string): empty {
  throw new RedirectError(to, false);
}

/** `redirect`, with a permanent status. */
export function permanentRedirect(to: string): empty {
  throw new RedirectError(to, true);
}
